import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import SEO from "../components/SEO";
import { Buildings, CheckCircle, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";

const BUSINESS_TYPES = [
  { v: "retailer", label: "Retailer / Shop" },
  { v: "reseller", label: "Reseller" },
  { v: "system_integrator", label: "System Integrator" },
  { v: "installer", label: "Installer / Service Provider" },
  { v: "ecommerce", label: "E-commerce Seller" },
];

const TURNOVERS = ["< 5 Lakh", "5-10 Lakh", "10-25 Lakh", "25-50 Lakh", "50 Lakh - 1 Cr", "1-5 Cr", "5+ Cr"];

const DealerRegister = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", name: "", password: "", password2: "",
    company_name: "", company_gstin: "", company_address: "",
    phone: "", territory: "", business_type: "", annual_turnover: "", references: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const upd = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.name || !form.password || !form.company_name || !form.phone) {
      toast.error("Please fill all required fields");
      return;
    }
    if (form.password.length < 6) { toast.error("Password must be 6+ chars"); return; }
    if (form.password !== form.password2) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      delete payload.password2;
      await axios.post(`${API}/auth/register-dealer`, payload);
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Registration failed"); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
        <SEO title="Application submitted — SwifTech India" description="Your dealer application is pending approval" />
        <div className="bg-white shadow-xl max-w-lg w-full p-10 text-center" data-testid="dealer-register-success">
          <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" weight="fill" />
          <h1 className="text-2xl font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Application Submitted!</h1>
          <p className="text-sm text-slate-600 mb-6">Thanks for applying, <b>{form.name}</b>. Our team reviews applications within 24 hours. You'll get an email at <b>{form.email}</b> once approved.</p>
          <div className="bg-blue-50 border border-blue-200 rounded p-4 text-left text-xs text-slate-700 mb-5">
            <div className="font-bold text-[#002FA7] mb-1">What happens next?</div>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Admin verifies your GSTIN & business details</li>
              <li>Approval email sent to {form.email}</li>
              <li>Login & enjoy dealer pricing on 100+ products</li>
              <li>Download dealer price list PDF anytime</li>
            </ol>
          </div>
          <Link to="/auth" className="inline-flex items-center gap-2 text-sm text-[#002FA7] font-bold hover:underline"><ArrowLeft size={14} /> Back to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <SEO title="Become a Dealer — SwifTech India" description="Apply for a dealer account to get exclusive pricing on CCTV, security systems & home automation" />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <Link to="/auth" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"><ArrowLeft size={14} /> Back to login</Link>
        </div>

        <div className="bg-white shadow-xl">
          <div className="bg-gradient-to-br from-[#001a5c] to-[#002FA7] text-white p-8">
            <div className="flex items-center gap-3">
              <Buildings size={32} weight="fill" className="text-amber-400" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-blue-200">Dealer Programme</div>
                <h1 className="text-2xl md:text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Become a SwifTech Dealer</h1>
              </div>
            </div>
            <p className="text-sm text-blue-100 mt-3 max-w-xl">Unlock dealer pricing (up to 25% off retail), bulk pricing on 50+ units, and downloadable price lists. Applications reviewed within 24 hours.</p>
          </div>

          <form onSubmit={submit} className="p-8 space-y-6" data-testid="dealer-register-form">
            <Section title="Login Details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Your Full Name *"><input data-testid="dr-name" value={form.name} onChange={e => upd({ name: e.target.value })} required className={inp} /></Field>
                <Field label="Email (for login) *"><input data-testid="dr-email" type="email" value={form.email} onChange={e => upd({ email: e.target.value })} required className={inp} /></Field>
                <Field label="Password *"><input data-testid="dr-password" type="password" value={form.password} onChange={e => upd({ password: e.target.value })} minLength={6} required className={inp} /></Field>
                <Field label="Confirm Password *"><input type="password" value={form.password2} onChange={e => upd({ password2: e.target.value })} required className={inp} /></Field>
              </div>
            </Section>

            <Section title="Company Details">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Company Name *"><input data-testid="dr-company" value={form.company_name} onChange={e => upd({ company_name: e.target.value })} required className={inp} /></Field>
                <Field label="GSTIN"><input data-testid="dr-gstin" value={form.company_gstin} onChange={e => upd({ company_gstin: e.target.value.toUpperCase() })} placeholder="29ABCDE1234F1ZA" className={`${inp} font-mono`} /></Field>
                <Field label="Phone *"><input data-testid="dr-phone" value={form.phone} onChange={e => upd({ phone: e.target.value })} placeholder="+91 98765 43210" required className={inp} /></Field>
                <Field label="Territory"><input value={form.territory} onChange={e => upd({ territory: e.target.value })} placeholder="e.g. Karnataka, South India" className={inp} /></Field>
              </div>
              <Field label="Company Address"><textarea rows={2} value={form.company_address} onChange={e => upd({ company_address: e.target.value })} className={inp} /></Field>
            </Section>

            <Section title="Business Profile">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Business Type">
                  <select value={form.business_type} onChange={e => upd({ business_type: e.target.value })} className={inp}>
                    <option value="">— Select —</option>
                    {BUSINESS_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="Annual Turnover">
                  <select value={form.annual_turnover} onChange={e => upd({ annual_turnover: e.target.value })} className={inp}>
                    <option value="">— Select —</option>
                    {TURNOVERS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="References / Notes"><textarea rows={2} value={form.references} onChange={e => upd({ references: e.target.value })} placeholder="Any existing dealer relationships, notable clients, etc." className={inp} /></Field>
            </Section>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex items-start gap-2">
              <ShieldCheck size={16} className="text-amber-600 shrink-0 mt-0.5" weight="fill" />
              <span>Your application will be reviewed manually. Please ensure GSTIN and company details are accurate. Your password will be activated only after admin approval.</span>
            </div>

            <button type="submit" disabled={submitting} data-testid="dr-submit"
              className="w-full bg-[#002FA7] text-white hover:bg-[#00227A] py-3.5 font-bold text-sm disabled:opacity-60">
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
            <p className="text-center text-xs text-slate-500">Already a dealer? <Link to="/auth" className="text-[#002FA7] font-bold hover:underline">Login here</Link></p>
          </form>
        </div>
      </div>
    </div>
  );
};

const inp = "w-full border border-slate-300 rounded p-2.5 text-sm outline-none focus:border-[#002FA7] bg-white";
const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3 pb-2 border-b border-slate-200">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);
const Field = ({ label, children }) => (
  <div><label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">{label}</label>{children}</div>
);

export default DealerRegister;
